import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, CircularProgress, Alert, Chip, Button, Tabs, Tab, Paper,
  TextField, Stack, Divider, Table, TableHead, TableRow, TableCell, TableBody,
  TableContainer, Dialog, DialogTitle, DialogContent, DialogActions,
  IconButton, Tooltip, FormControlLabel, Checkbox, Select, MenuItem,
  FormControl, InputLabel, TableSortLabel,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DownloadIcon from '@mui/icons-material/Download';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useParams, useNavigate } from 'react-router-dom';
import TopNavbar from '../../components/TopNavBar/TopNavBar';
import AdminPortalSideBar from '../../containers/AdminSideBarContainer/AdminSideBarContainer';
import { PageContainer, ContentContainer } from '../AdminPortalPage/AdminPortalPage.styles';
import {
  getCustomerProfile, updateCustomerProfile, freezeCustomer, unfreezeCustomer,
  getCustomerOrders, exportCustomerOrders, getCustomerSubUsers,
  adminCreateCustomerSubUser, adminUpdateCustomerSubUser, adminDeactivateCustomerSubUser,
  adminTransferCompanyPrimary, adminGetCompanyOwnershipHistory,
} from '../../api/api';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import { useToast } from '../../components/ToastProvider/ToastProvider';
import { useAuth } from '../../contexts/AuthContext';
import CountrySelect from '../../components/CountriesDropBox/CountriesDropBox';

function TabPanel({ children, value, index }) {
  return value === index ? <Box sx={{ pt: 2 }}>{children}</Box> : null;
}

const EMPTY_ADDRESS = {
  label: '', recipient_name: '', phone_number: '',
  street_address: '', suburb_locality: '', city_town: '',
  state_province_region: '', postal_zip_code: '', country: '',
  is_billing: false, is_shipping: true, is_default: false,
};

const emptyBusinessInfo = () => ({
  legal_business_name: '',
  registration_number: '',
  vat_tax_number: '',
  eori_import_export_number: '',
});

const emptySubUserForm = () => ({
  full_name: '',
  email: '',
  mobile_no: '',
  password: '',
  company_role: '',
  is_active: true,
});

export default function CustomerDetailPage() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { hasPermission } = useAuth();
  const canManageCustomers = hasPermission('customers.manage');

  const [tab, setTab] = useState(0);
  const [profile, setProfile] = useState(null);
  const [orders, setOrders] = useState([]);
  const [orderSummary, setOrderSummary] = useState({ total_orders: 0, total_paid: 0 });
  const [loading, setLoading] = useState(true);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [error, setError] = useState(null);

  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [editAddresses, setEditAddresses] = useState([]);
  const [saving, setSaving] = useState(false);

  const [freezeDialogOpen, setFreezeDialogOpen] = useState(false);
  const [freezing, setFreezing] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [orderStatusFilter, setOrderStatusFilter] = useState('all');
  const [dateSortDir, setDateSortDir] = useState('desc');
  const [subUsers, setSubUsers] = useState([]);
  const [subUsersLoading, setSubUsersLoading] = useState(false);
  const [subDialogOpen, setSubDialogOpen] = useState(false);
  const [subDialogMode, setSubDialogMode] = useState('add');
  const [subForm, setSubForm] = useState(emptySubUserForm);
  const [subEditId, setSubEditId] = useState(null);
  const [subSaving, setSubSaving] = useState(false);

  const [transferOpen, setTransferOpen] = useState(false);
  const [transferTargetId, setTransferTargetId] = useState('');
  const [transferReason, setTransferReason] = useState('');
  const [transferDeactivateOld, setTransferDeactivateOld] = useState(false);
  const [transferring, setTransferring] = useState(false);
  const [ownershipHistory, setOwnershipHistory] = useState([]);
  const [ownershipHistoryLoading, setOwnershipHistoryLoading] = useState(false);

  const fetchProfile = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getCustomerProfile(userId);
      setProfile(data);
      setEditForm({
        full_name: data.full_name || '',
        mobile_no: data.mobile_no || '',
        account_type: data.account_type || 'Individual',
        business_info: data.business_info
          ? { ...emptyBusinessInfo(), ...data.business_info }
          : emptyBusinessInfo(),
      });
      setEditAddresses(
        (data.addresses || []).map(a => ({ ...a }))
      );
      if (data.account_type === 'Business' && !data.business_owner_id) {
        setSubUsersLoading(true);
        try {
          const su = await getCustomerSubUsers(userId);
          setSubUsers(Array.isArray(su) ? su : []);
        } catch {
          setSubUsers([]);
        } finally {
          setSubUsersLoading(false);
        }
      } else {
        setSubUsers([]);
      }
    } catch (e) {
      setError(e.message || 'Failed to load customer profile');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  const fetchOrders = useCallback(async () => {
    setOrdersLoading(true);
    try {
      const data = await getCustomerOrders(userId);
      setOrders(Array.isArray(data.orders) ? data.orders : Array.isArray(data) ? data : []);
      if (data.summary) setOrderSummary(data.summary);
    } catch {
      setOrders([]);
    } finally {
      setOrdersLoading(false);
    }
  }, [userId]);

  useEffect(() => { fetchProfile(); }, [fetchProfile]);
  useEffect(() => { if (tab === 1) fetchOrders(); }, [tab, fetchOrders]);

  const startEditing = () => {
    setEditForm({
      full_name: profile.full_name || '',
      mobile_no: profile.mobile_no || '',
      account_type: profile.account_type || 'Individual',
      business_info: profile.business_info
        ? { ...emptyBusinessInfo(), ...profile.business_info }
        : emptyBusinessInfo(),
    });
    setEditAddresses(
      (profile.addresses || []).map(a => ({ ...a }))
    );
    setEditing(true);
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      const { business_info: biz, ...profileFields } = editForm;
      const payload = {
        ...profileFields,
        addresses: editAddresses.map(({ id, ...rest }) => rest),
      };
      const isBizPrimary =
        profile.account_type === 'Business' && !profile.business_owner_id;
      if (isBizPrimary) {
        payload.business_info = biz;
      }
      await updateCustomerProfile(userId, payload);
      showToast('Profile updated', 'success');
      setEditing(false);
      fetchProfile();
    } catch (err) {
      showToast(err.message || 'Failed to update profile', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleFreezeToggle = async () => {
    setFreezing(true);
    try {
      if (profile.is_active) {
        await freezeCustomer(userId);
      } else {
        await unfreezeCustomer(userId);
      }
      showToast(`Customer ${profile.is_active ? 'frozen' : 'unfrozen'} successfully`, 'success');
      setFreezeDialogOpen(false);
      fetchProfile();
    } catch (err) {
      showToast(err.message || 'Operation failed', 'error');
    } finally {
      setFreezing(false);
    }
  };

  const handleExportOrders = async () => {
    setExporting(true);
    try {
      const blob = await exportCustomerOrders(userId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `customer_${userId}_orders.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      showToast('Orders exported', 'success');
    } catch (err) {
      showToast(err.message || 'Export failed', 'error');
    } finally {
      setExporting(false);
    }
  };

  const updateAddress = (idx, field, value) => {
    setEditAddresses(prev => prev.map((a, i) => i === idx ? { ...a, [field]: value } : a));
  };

  const addAddress = () => {
    setEditAddresses(prev => [...prev, { ...EMPTY_ADDRESS }]);
  };

  const removeAddress = (idx) => {
    setEditAddresses(prev => prev.filter((_, i) => i !== idx));
  };

  const openAddSubUser = () => {
    setSubDialogMode('add');
    setSubEditId(null);
    setSubForm(emptySubUserForm());
    setSubDialogOpen(true);
  };

  const openEditSubUser = su => {
    setSubDialogMode('edit');
    setSubEditId(su.id);
    setSubForm({
      full_name: su.full_name || '',
      email: su.email || '',
      mobile_no: su.mobile_no || '',
      password: '',
      company_role: su.company_role || '',
      is_active: su.is_active !== false,
    });
    setSubDialogOpen(true);
  };

  const handleSaveSubUser = async () => {
    if (!canManageCustomers) return;
    setSubSaving(true);
    try {
      if (subDialogMode === 'add') {
        if (!subForm.password || subForm.password.length < 8) {
          showToast('Password must be at least 8 characters', 'error');
          setSubSaving(false);
          return;
        }
        await adminCreateCustomerSubUser(userId, {
          full_name: subForm.full_name.trim(),
          email: subForm.email.trim(),
          mobile_no: subForm.mobile_no.trim(),
          password: subForm.password,
          company_role: subForm.company_role.trim() || null,
        });
        showToast('Company user added', 'success');
      } else {
        const payload = {
          full_name: subForm.full_name.trim(),
          email: subForm.email.trim(),
          mobile_no: subForm.mobile_no.trim(),
          is_active: subForm.is_active,
          company_role: subForm.company_role.trim() || null,
        };
        if (subForm.password) {
          if (subForm.password.length < 8) {
            showToast('Password must be at least 8 characters', 'error');
            setSubSaving(false);
            return;
          }
          payload.password = subForm.password;
        }
        await adminUpdateCustomerSubUser(userId, subEditId, payload);
        showToast('Company user updated', 'success');
      }
      setSubDialogOpen(false);
      fetchProfile();
    } catch (err) {
      showToast(err.message || 'Request failed', 'error');
    } finally {
      setSubSaving(false);
    }
  };

  const handleDeactivateSubUser = async subId => {
    if (!canManageCustomers) return;
    if (!window.confirm('Deactivate this company user? They will no longer be able to sign in.')) return;
    setSubSaving(true);
    try {
      await adminDeactivateCustomerSubUser(userId, subId);
      showToast('User deactivated', 'success');
      fetchProfile();
    } catch (err) {
      showToast(err.message || 'Request failed', 'error');
    } finally {
      setSubSaving(false);
    }
  };

  const openTransferPrimary = async () => {
    if (!canManageCustomers || !profile?.company_id) return;
    setTransferTargetId('');
    setTransferReason('');
    setTransferDeactivateOld(true);
    setTransferOpen(true);
    setOwnershipHistoryLoading(true);
    try {
      const h = await adminGetCompanyOwnershipHistory(profile.company_id);
      setOwnershipHistory(Array.isArray(h) ? h : []);
    } catch {
      setOwnershipHistory([]);
    } finally {
      setOwnershipHistoryLoading(false);
    }
  };

  const handleTransferPrimary = async () => {
    if (!canManageCustomers || !profile?.company_id) return;
    if (!transferTargetId) {
      showToast('Please pick a company user to promote', 'warning');
      return;
    }
    if (!transferReason.trim()) {
      showToast('Please add a reason for the audit trail', 'warning');
      return;
    }
    if (!window.confirm(
      'Transfer primary ownership? The current primary will be demoted to a company member'
      + (transferDeactivateOld ? ' and deactivated (login blocked).' : '.')
      + '\n\nThis action is audited and can only be reversed by another transfer.'
    )) return;
    setTransferring(true);
    try {
      await adminTransferCompanyPrimary(profile.company_id, {
        new_primary_user_id: Number(transferTargetId),
        reason: transferReason.trim() || null,
        deactivate_old_primary: transferDeactivateOld,
      });
      showToast('Primary ownership transferred', 'success');
      setTransferOpen(false);
      // Navigate to the new primary user's page so admin can see the updated state
      const newPrimaryUserId = Number(transferTargetId);
      navigate(`/customer-management/${newPrimaryUserId}`);
    } catch (err) {
      showToast(err.message || 'Transfer failed', 'error');
    } finally {
      setTransferring(false);
    }
  };

  const formatAddress = (addr) => {
    const parts = [addr.street_address, addr.suburb_locality, addr.city_town, addr.state_province_region, addr.postal_zip_code, addr.country].filter(Boolean);
    return parts.join(', ');
  };

  const getStatusStyle = (status) => {
    if (!status) return { backgroundColor: '#e0e0e0', color: '#616161' };
    const s = status.toUpperCase();
    if (s === 'PAID')                       return { backgroundColor: '#e8f5e9', color: '#2e7d32' };
    if (s === 'DELIVERED' || s === 'COMPLETED') return { backgroundColor: '#e0f2f1', color: '#00695c' };
    if (s === 'SHIPPED')                    return { backgroundColor: '#e3f2fd', color: '#1565c0' };
    if (s === 'ASSEMBLING ORDER')           return { backgroundColor: '#fff3e0', color: '#e65100' };
    if (s.includes('VENDOR VALIDATION'))    return { backgroundColor: '#fff8e1', color: '#f57f17' };
    if (s.includes('VENDOR ORDER'))          return { backgroundColor: '#e8eaf6', color: '#283593' };
    if (s.includes('AWAITING PAYMENT') || s.includes('WAITING PAYMENT')) return { backgroundColor: '#fce4ec', color: '#c62828' };
    if (s === 'CANCELLED')                  return { backgroundColor: '#fbe9e7', color: '#bf360c' };
    if (s.includes('AWAITING') || s.includes('PENDING')) return { backgroundColor: '#f3e5f5', color: '#6a1b9a' };
    return { backgroundColor: '#e8eaf6', color: '#283593' };
  };

  return (
    <>
      <TopNavbar />
      <PageContainer>
        <AdminPortalSideBar />
        <ContentContainer>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <IconButton onClick={() => navigate('/customer-management')}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
              Customer Details
            </Typography>
            {profile && (
              <Chip
                label={profile.is_active ? 'Active' : 'Frozen'}
                color={profile.is_active ? 'success' : 'error'}
                size="small"
                sx={{ ml: 1 }}
              />
            )}
          </Box>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
          ) : profile ? (
            <>
              <Paper sx={{ mb: 2 }}>
                <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
                  <Tab label="Profile" />
                  <Tab label="Order History" />
                </Tabs>
              </Paper>

              {/* ── PROFILE TAB ── */}
              <TabPanel value={tab} index={0}>
                <Paper sx={{ p: 3 }}>
                  {canManageCustomers && (
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 2 }}>
                      {!editing && (
                        <Button startIcon={<EditIcon />} variant="outlined" size="small" onClick={startEditing}>
                          Edit Profile
                        </Button>
                      )}
                      <Button
                        startIcon={profile.is_active ? <LockIcon /> : <LockOpenIcon />}
                        variant="outlined"
                        size="small"
                        color={profile.is_active ? 'warning' : 'success'}
                        onClick={() => setFreezeDialogOpen(true)}
                      >
                        {profile.is_active ? 'Freeze' : 'Unfreeze'}
                      </Button>
                    </Box>
                  )}

                  {editing ? (
                    <Stack spacing={2.5} sx={{ maxWidth: 600 }}>
                      {profile.account_type === 'Business' && !profile.business_owner_id && (
                        <>
                          <Typography variant="h6">Business information</Typography>
                          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                            <TextField
                              label="Company name"
                              size="small"
                              fullWidth
                              value={editForm.business_info?.legal_business_name || ''}
                              onChange={e => setEditForm(f => ({
                                ...f,
                                business_info: { ...f.business_info, legal_business_name: e.target.value },
                              }))}
                            />
                            <TextField
                              label="Registration no. (optional)"
                              size="small"
                              fullWidth
                              value={editForm.business_info?.registration_number || ''}
                              onChange={e => setEditForm(f => ({
                                ...f,
                                business_info: { ...f.business_info, registration_number: e.target.value },
                              }))}
                            />
                            <TextField
                              label="VAT No. (optional)"
                              size="small"
                              fullWidth
                              value={editForm.business_info?.vat_tax_number || ''}
                              onChange={e => setEditForm(f => ({
                                ...f,
                                business_info: { ...f.business_info, vat_tax_number: e.target.value },
                              }))}
                            />
                            <TextField
                              label="EORI / Import-export no."
                              size="small"
                              fullWidth
                              value={editForm.business_info?.eori_import_export_number || ''}
                              onChange={e => setEditForm(f => ({
                                ...f,
                                business_info: { ...f.business_info, eori_import_export_number: e.target.value },
                              }))}
                            />
                          </Box>
                          <Divider />
                        </>
                      )}

                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="h6">Addresses</Typography>
                        <Button size="small" variant="outlined" onClick={addAddress}>+ Add Address</Button>
                      </Box>

                      {editAddresses.map((addr, idx) => (
                        <Paper key={idx} variant="outlined" sx={{ p: 2 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Typography variant="subtitle2">Address {idx + 1}</Typography>
                            <Button size="small" color="error" onClick={() => removeAddress(idx)}>Remove</Button>
                          </Box>
                          <Stack spacing={1.5}>
                            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                              <TextField label="Label" size="small" value={addr.label || ''} onChange={e => updateAddress(idx, 'label', e.target.value)} />
                              <TextField label="Recipient Name" size="small" value={addr.recipient_name || ''} onChange={e => updateAddress(idx, 'recipient_name', e.target.value)} />
                            </Box>
                            <TextField label="Street Address" size="small" fullWidth value={addr.street_address || ''} onChange={e => updateAddress(idx, 'street_address', e.target.value)} />
                            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                              <TextField label="Suburb / Locality" size="small" value={addr.suburb_locality || ''} onChange={e => updateAddress(idx, 'suburb_locality', e.target.value)} />
                              <TextField label="City / Town" size="small" value={addr.city_town || ''} onChange={e => updateAddress(idx, 'city_town', e.target.value)} />
                            </Box>
                            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1.5 }}>
                              <TextField label="State / Region" size="small" value={addr.state_province_region || ''} onChange={e => updateAddress(idx, 'state_province_region', e.target.value)} />
                              <TextField label="Postal / Zip Code" size="small" value={addr.postal_zip_code || ''} onChange={e => updateAddress(idx, 'postal_zip_code', e.target.value)} />
                              <CountrySelect
                                value={addr.country || ''}
                                onChange={val => updateAddress(idx, 'country', val)}
                              />
                            </Box>
                            <TextField label="Phone Number" size="small" value={addr.phone_number || ''} onChange={e => updateAddress(idx, 'phone_number', e.target.value)} />
                            <Box sx={{ display: 'flex', gap: 2 }}>
                              <FormControlLabel
                                control={<Checkbox checked={!!addr.is_billing} onChange={e => updateAddress(idx, 'is_billing', e.target.checked)} size="small" />}
                                label="Billing"
                              />
                              <FormControlLabel
                                control={<Checkbox checked={!!addr.is_shipping} onChange={e => updateAddress(idx, 'is_shipping', e.target.checked)} size="small" />}
                                label="Shipping"
                              />
                              <FormControlLabel
                                control={<Checkbox checked={!!addr.is_default} onChange={e => updateAddress(idx, 'is_default', e.target.checked)} size="small" />}
                                label="Default"
                              />
                            </Box>
                          </Stack>
                        </Paper>
                      ))}

                      <Divider />
                      <Typography variant="h6">Profile information</Typography>
                      <TextField
                        label="Full Name" size="small" fullWidth
                        value={editForm.full_name}
                        onChange={e => setEditForm(f => ({ ...f, full_name: e.target.value }))}
                      />
                      <TextField
                        label="Phone" size="small" fullWidth
                        value={editForm.mobile_no}
                        onChange={e => setEditForm(f => ({ ...f, mobile_no: e.target.value }))}
                      />
                      <TextField label="Email" size="small" fullWidth value={profile.email || ''} disabled helperText="Email cannot be changed" />
                      <TextField
                        label="Account Type" size="small" fullWidth
                        value={editForm.account_type}
                        onChange={e => setEditForm(f => ({ ...f, account_type: e.target.value }))}
                      />

                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button variant="contained" onClick={handleSaveProfile} disabled={saving}>
                          {saving ? 'Saving...' : 'Save'}
                        </Button>
                        <Button variant="outlined" onClick={() => setEditing(false)}>Cancel</Button>
                      </Box>
                    </Stack>
                  ) : (
                    <Stack spacing={2}>
                      {/* Business Information — shown first for any Business
                          account (primary or member). Editing remains
                          primary-only and lives in the edit form above. */}
                      {profile.account_type === 'Business' && profile.business_info && (
                        <>
                          <Typography variant="h6">Business Information</Typography>
                          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                            <Box>
                              <Typography variant="subtitle2" color="text.secondary">Company Name</Typography>
                              <Typography>{profile.business_info?.legal_business_name || '—'}</Typography>
                            </Box>
                            <Box>
                              <Typography variant="subtitle2" color="text.secondary">Registration No.</Typography>
                              <Typography>{profile.business_info?.registration_number || '—'}</Typography>
                            </Box>
                            <Box>
                              <Typography variant="subtitle2" color="text.secondary">VAT No.</Typography>
                              <Typography>{profile.business_info?.vat_tax_number || '—'}</Typography>
                            </Box>
                            <Box>
                              <Typography variant="subtitle2" color="text.secondary">EORI / Import-Export No.</Typography>
                              <Typography>{profile.business_info?.eori_import_export_number || '—'}</Typography>
                            </Box>
                          </Box>
                          <Divider sx={{ my: 1 }} />
                        </>
                      )}

                      {profile.addresses && profile.addresses.length > 0 && (
                        <>
                          <Typography variant="h6">Addresses</Typography>
                          {profile.addresses.map((addr, i) => (
                            <Box key={addr.id || i} sx={{ pl: 2, borderLeft: '3px solid #e0e0e0', mb: 1 }}>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {addr.is_billing && 'Billing'}{addr.is_billing && addr.is_shipping && ' & '}{addr.is_shipping && 'Shipping'}
                                {addr.label ? ` — ${addr.label}` : ''}
                                {addr.is_default && ' (Default)'}
                              </Typography>
                              {addr.recipient_name && <Typography variant="body2">{addr.recipient_name}</Typography>}
                              <Typography variant="body2" color="text.secondary">{formatAddress(addr)}</Typography>
                              {addr.phone_number && <Typography variant="body2" color="text.secondary">Phone: {addr.phone_number}</Typography>}
                            </Box>
                          ))}
                          <Divider sx={{ my: 1 }} />
                        </>
                      )}

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="h6">Profile Information</Typography>
                        {profile.account_type === 'Business' && (
                          profile.business_owner_id ? (
                            <Chip label="Member" size="small" color="default" variant="outlined" />
                          ) : (
                            <Chip label="Primary" size="small" color="primary" />
                          )
                        )}
                      </Box>
                      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                        <Box>
                          <Typography variant="subtitle2" color="text.secondary">Full Name</Typography>
                          <Typography>{profile.full_name || '—'}</Typography>
                        </Box>
                        <Box>
                          <Typography variant="subtitle2" color="text.secondary">Email</Typography>
                          <Typography>{profile.email || '—'}</Typography>
                        </Box>
                        <Box>
                          <Typography variant="subtitle2" color="text.secondary">Phone</Typography>
                          <Typography>{profile.mobile_no || '—'}</Typography>
                        </Box>
                        <Box>
                          <Typography variant="subtitle2" color="text.secondary">Account Type</Typography>
                          <Typography>{profile.account_type || 'Individual'}</Typography>
                        </Box>
                      </Box>

                    </Stack>
                  )}

                  {profile.account_type === 'Business' && !profile.business_owner_id && (
                    <>
                      <Divider sx={{ my: 2 }} />
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1 }}>
                        <Box>
                          <Typography variant="h6">Company users</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {canManageCustomers
                              ? 'Add, edit, or deactivate users under this business account.'
                              : 'Users linked to this business account.'}
                          </Typography>
                        </Box>
                        {canManageCustomers && (
                          <Stack direction="row" spacing={1} alignItems="center">
                            {profile.company_id && (
                              <Tooltip title="Promote an existing company user to primary and demote the current owner. Used when the primary user leaves the company.">
                                <span>
                                  <Button
                                    startIcon={<SwapHorizIcon />}
                                    variant="outlined"
                                    color="warning"
                                    size="small"
                                    onClick={openTransferPrimary}
                                    disabled={subUsers.filter(s => s.is_active).length === 0}
                                  >
                                    Transfer primary
                                  </Button>
                                </span>
                              </Tooltip>
                            )}
                            <Button startIcon={<PersonAddIcon />} variant="contained" size="small" onClick={openAddSubUser}>
                              Add user
                            </Button>
                          </Stack>
                        )}
                      </Box>
                      {subUsersLoading ? (
                        <CircularProgress size={24} />
                      ) : subUsers.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">No additional company users.</Typography>
                      ) : (
                        <TableContainer component={Paper} variant="outlined">
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Name</TableCell>
                                <TableCell>Email</TableCell>
                                <TableCell>Phone</TableCell>
                                <TableCell>Role</TableCell>
                                <TableCell>Active</TableCell>
                                {canManageCustomers && <TableCell align="right">Actions</TableCell>}
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {subUsers.map(su => (
                                <TableRow key={su.id}>
                                  <TableCell>{su.full_name}</TableCell>
                                  <TableCell>{su.email}</TableCell>
                                  <TableCell>{su.mobile_no}</TableCell>
                                  <TableCell>{su.company_role || '—'}</TableCell>
                                  <TableCell>{su.is_active ? 'Yes' : 'No'}</TableCell>
                                  {canManageCustomers && (
                                    <TableCell align="right">
                                      <Tooltip title="Edit user">
                                        <IconButton size="small" onClick={() => openEditSubUser(su)}>
                                          <EditIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                      <Tooltip title="Deactivate user">
                                        <IconButton
                                          size="small"
                                          color="error"
                                          disabled={subSaving}
                                          onClick={() => handleDeactivateSubUser(su.id)}
                                        >
                                          <DeleteOutlineIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    </TableCell>
                                  )}
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      )}
                    </>
                  )}
                </Paper>
              </TabPanel>

              {/* ── ORDER HISTORY TAB ── */}
              <TabPanel value={tab} index={1}>
                <Paper sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">Order History</Typography>
                    {canManageCustomers && (
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<DownloadIcon />}
                      onClick={handleExportOrders}
                      disabled={exporting || orders.length === 0}
                    >
                      {exporting ? 'Exporting...' : 'Export Orders'}
                    </Button>
                    )}
                  </Box>

                  {orderSummary.total_paid > 0 && (
                    <Alert severity="info" icon={false} sx={{ mb: 2 }}>
                      <Typography variant="body2">
                        <strong>Total Amount Paid:</strong> ${orderSummary.total_paid.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </Typography>
                    </Alert>
                  )}

                  {orders.length > 0 && (
                    <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
                      <FormControl size="small" sx={{ minWidth: 220 }}>
                        <InputLabel>Filter by Status</InputLabel>
                        <Select
                          value={orderStatusFilter}
                          label="Filter by Status"
                          onChange={e => setOrderStatusFilter(e.target.value)}
                        >
                          <MenuItem value="all">All Statuses</MenuItem>
                          {[...new Set(orders.map(o => o.status).filter(Boolean))].sort().map(s => (
                            <MenuItem key={s} value={s}>{s}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <Typography variant="body2" color="text.secondary">
                        Showing {(() => {
                          const filtered = orders.filter(o => orderStatusFilter === 'all' || o.status === orderStatusFilter);
                          return `${filtered.length} of ${orders.length}`;
                        })()} order{orders.length !== 1 ? 's' : ''}
                      </Typography>
                    </Box>
                  )}

                  {ordersLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
                  ) : orders.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">No orders found for this customer.</Typography>
                  ) : (() => {
                    const filteredOrders = orders
                      .filter(o => orderStatusFilter === 'all' || o.status === orderStatusFilter)
                      .sort((a, b) => {
                        const da = a.created_at ? new Date(a.created_at).getTime() : 0;
                        const db = b.created_at ? new Date(b.created_at).getTime() : 0;
                        return dateSortDir === 'asc' ? da - db : db - da;
                      });

                    return filteredOrders.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">No orders match the selected status.</Typography>
                    ) : (
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                              <TableCell sx={{ fontWeight: 'bold' }}>Order #</TableCell>
                              <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                              <TableCell sx={{ fontWeight: 'bold' }} sortDirection={dateSortDir}>
                                <TableSortLabel
                                  active
                                  direction={dateSortDir}
                                  onClick={() => setDateSortDir(prev => prev === 'asc' ? 'desc' : 'asc')}
                                >
                                  Date
                                </TableSortLabel>
                              </TableCell>
                              <TableCell align="right" sx={{ fontWeight: 'bold' }}>Total</TableCell>
                              <TableCell align="center" sx={{ fontWeight: 'bold' }}></TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {filteredOrders.map(o => (
                              <TableRow key={o.id} hover>
                                <TableCell>{o.order_number || `#${o.id}`}</TableCell>
                                <TableCell>
                                  <Chip label={o.status} size="small" sx={{ fontWeight: 500, fontSize: '0.7rem', ...getStatusStyle(o.status) }} />
                                </TableCell>
                                <TableCell>
                                  {o.created_at ? new Date(o.created_at).toLocaleDateString() : '—'}
                                </TableCell>
                                <TableCell align="right">
                                  ${(o.total_amount || 0).toFixed(2)}
                                </TableCell>
                                <TableCell align="center">
                                  <Tooltip title="Open in Order Management">
                                    <IconButton
                                      size="small"
                                      onClick={() => navigate(`/order-management?orderId=${o.id}`)}
                                    >
                                      <OpenInNewIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    );
                  })()}
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Total: {orders.length} order{orders.length !== 1 ? 's' : ''}
                  </Typography>
                </Paper>
              </TabPanel>
            </>
          ) : null}
        </ContentContainer>
      </PageContainer>

      {/* Freeze/Unfreeze Confirmation */}
      <Dialog open={subDialogOpen} onClose={() => !subSaving && setSubDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{subDialogMode === 'add' ? 'Add company user' : 'Edit company user'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Full name"
              size="small"
              fullWidth
              value={subForm.full_name}
              onChange={e => setSubForm(f => ({ ...f, full_name: e.target.value }))}
            />
            <TextField
              label="Email"
              size="small"
              fullWidth
              type="email"
              value={subForm.email}
              onChange={e => setSubForm(f => ({ ...f, email: e.target.value }))}
            />
            <TextField
              label="Phone"
              size="small"
              fullWidth
              value={subForm.mobile_no}
              onChange={e => setSubForm(f => ({ ...f, mobile_no: e.target.value }))}
            />
            <TextField
              label="Company role"
              size="small"
              fullWidth
              value={subForm.company_role}
              onChange={e => setSubForm(f => ({ ...f, company_role: e.target.value }))}
              helperText="Examples: Admin, Buyer, Warehouse, Billing"
            />
            {subDialogMode === 'add' ? (
              <TextField
                label="Password"
                size="small"
                fullWidth
                type="password"
                autoComplete="new-password"
                value={subForm.password}
                onChange={e => setSubForm(f => ({ ...f, password: e.target.value }))}
                helperText="Minimum 8 characters"
              />
            ) : (
              <>
                <TextField
                  label="New password (optional)"
                  size="small"
                  fullWidth
                  type="password"
                  autoComplete="new-password"
                  value={subForm.password}
                  onChange={e => setSubForm(f => ({ ...f, password: e.target.value }))}
                  helperText="Leave blank to keep current password"
                />
                <FormControlLabel
                  control={(
                    <Checkbox
                      checked={subForm.is_active}
                      onChange={e => setSubForm(f => ({ ...f, is_active: e.target.checked }))}
                      size="small"
                    />
                  )}
                  label="Account active"
                />
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSubDialogOpen(false)} disabled={subSaving}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveSubUser} disabled={subSaving}>
            {subSaving ? 'Saving…' : subDialogMode === 'add' ? 'Add user' : 'Save changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Transfer primary ownership dialog ── */}
      <Dialog
        open={transferOpen}
        onClose={() => !transferring && setTransferOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 'bold' }}>
          Transfer primary ownership
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            The selected user becomes the new primary for{' '}
            <strong>{profile?.business_info?.legal_business_name || 'this company'}</strong>.
            The current primary (<strong>{profile?.full_name || profile?.email}</strong>) will
            be demoted to a company member{transferDeactivateOld ? ' and their login will be blocked' : ''}.
          </Alert>

          <FormControl fullWidth size="small" sx={{ mt: 1 }}>
            <InputLabel id="new-primary-label">New primary (pick an active company user)</InputLabel>
            <Select
              labelId="new-primary-label"
              label="New primary (pick an active company user)"
              value={transferTargetId}
              onChange={e => setTransferTargetId(e.target.value)}
            >
              {subUsers.filter(s => s.is_active).length === 0 && (
                <MenuItem value="" disabled>No active company users — add one first</MenuItem>
              )}
              {subUsers.filter(s => s.is_active).map(s => (
                <MenuItem key={s.id} value={s.id}>
                  {s.full_name || s.email} — {s.email}
                  {s.company_role ? ` · ${s.company_role}` : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Reason (audit trail)"
            value={transferReason}
            onChange={e => setTransferReason(e.target.value)}
            placeholder="e.g. Owner has left the company"
            required
            fullWidth size="small" multiline minRows={2} sx={{ mt: 2 }}
            helperText="Required — this is permanently recorded in the ownership history"
          />

          <FormControlLabel
            sx={{ mt: 1 }}
            control={
              <Checkbox
                checked={transferDeactivateOld}
                onChange={e => setTransferDeactivateOld(e.target.checked)}
              />
            }
            label="Deactivate old primary's login (recommended when they have left)"
          />

          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Ownership history</Typography>
          {ownershipHistoryLoading ? (
            <CircularProgress size={20} />
          ) : ownershipHistory.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No prior transfers for this company.</Typography>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>When</TableCell>
                    <TableCell>From</TableCell>
                    <TableCell>To</TableCell>
                    <TableCell>Reason</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {ownershipHistory.map(h => (
                    <TableRow key={h.id}>
                      <TableCell>{h.occurred_at?.replace('T', ' ').slice(0, 19) || '—'}</TableCell>
                      <TableCell>
                        {h.from_user_name || h.from_user_id
                          ? `${h.from_user_name || ''}${h.from_user_email ? ` (${h.from_user_email})` : ''}`
                          : '—'}
                      </TableCell>
                      <TableCell>
                        {h.to_user_name || h.to_user_email
                          ? `${h.to_user_name || ''}${h.to_user_email ? ` (${h.to_user_email})` : ''}`
                          : h.to_user_id}
                      </TableCell>
                      <TableCell>{h.reason || '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTransferOpen(false)} disabled={transferring}>Cancel</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleTransferPrimary}
            disabled={transferring || !transferTargetId || !transferReason.trim()}
            startIcon={<SwapHorizIcon />}
          >
            {transferring ? 'Transferring…' : 'Transfer primary'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={freezeDialogOpen} onClose={() => setFreezeDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold' }}>
          {profile?.is_active ? 'Freeze Customer Account' : 'Unfreeze Customer Account'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mt: 1 }}>
            {profile?.is_active
              ? 'Are you sure? This will prevent the customer from logging in or placing orders. Their email address will remain locked.'
              : 'This will restore the customer\'s ability to log in and place orders.'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFreezeDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color={profile?.is_active ? 'warning' : 'success'}
            onClick={handleFreezeToggle}
            disabled={freezing}
          >
            {freezing ? 'Processing...' : profile?.is_active ? 'Freeze' : 'Unfreeze'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
